import React, { useState, useEffect } from 'react';
import { useUser } from '@/shared/hooks/useUser';
import { Button } from '@/shared/ui/button';
import {
  ClientSelector,
  ProjectSelector,
  AlertMessage,
  UploadProgress,
} from '@/features/add-scan/components';
import { DroneTypeSelector, DateInput, GpsFileUpload, ZipFileUpload } from '../components';
import { useClients, useProjects } from '@/features/add-scan/hooks';
import {
  getDroneTypes,
  getSignedUrlForDroneZip,
  uploadZipToGCS,
  createDroneVideo,
} from '../api/drone-api';
import { DroneTypeChoice } from '../types';

export const UploadDroneForm: React.FC = () => {
  const { user } = useUser();

  // Form state
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedDroneType, setSelectedDroneType] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedGpsFile, setSelectedGpsFile] = useState<File | null>(null);

  // Drone types
  const [droneTypes, setDroneTypes] = useState<DroneTypeChoice[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);

  // UI state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Data hooks
  const { clients } = useClients(user?.is_staff || false);
  const { projects, isLoading: isLoadingProjects } = useProjects(selectedClient);

  // Load drone types on mount
  useEffect(() => {
    const loadDroneTypes = async () => {
      setIsLoadingTypes(true);
      try {
        const types = await getDroneTypes();
        console.log('📋 Drone types loaded:', types);
        console.log('📋 Is array?', Array.isArray(types));
        setDroneTypes(types);
      } catch (err) {
        console.error('❌ Error loading drone types:', err);
        setError('Failed to load drone types');
        setDroneTypes([]); // Set to empty array on error
      } finally {
        setIsLoadingTypes(false);
      }
    };

    loadDroneTypes();
  }, []);

  // Convert YYYY-MM-DD to MM.DD.YY format for backend
  const formatDateForBackend = (dateStr: string): string => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    const shortYear = year.slice(-2);
    return `${month}.${day}.${shortYear}`;
  };

  const resetFormAfterUpload = () => {
    setSelectedDroneType('');
    setDate('');
    setSelectedFile(null);
    setSelectedGpsFile(null);
    setUploadProgress(0);
    setUploadStatus('');
    setIsUploading(false);
  };

  const finalizeDroneRecordInBackground = async (payload: {
    project: number;
    drone_type: string;
    date: string;
    uploaded_zip_url: string;
    gps_data_url?: string;
  }) => {
    try {
      const result = await createDroneVideo(payload);
      if (!result.success) {
        setError(
          result.error ||
            'File upload succeeded, but we could not queue processing. Please refresh and try again.'
        );
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'File upload succeeded, but we could not queue processing. Please refresh and try again.';
      setError(errorMessage);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🚀 Form submission started');
    setError(null);
    setSuccess(null);

    // Validation
    if (!selectedClient || !selectedProject || !selectedDroneType || !date) {
      console.error('❌ Validation failed: Missing required fields');
      setError('Please fill in all required fields');
      return;
    }

    if (!selectedFile) {
      console.error('❌ Validation failed: No file selected');
      setError('Please select a zip file to upload');
      return;
    }

    console.log('📋 Form data:', {
      selectedClient,
      selectedProject,
      selectedDroneType,
      date,
      fileName: selectedFile.name
    });

    try {
      setIsUploading(true);
      setUploadStatus('Preparing upload...');
      setUploadProgress(0);

      // Convert date to backend format (MM.DD.YY)
      const formattedDate = formatDateForBackend(date);
      console.log('📅 Formatted date:', formattedDate);

      // Upload the small optional sidecar first so a GPS failure never leaves
      // a large, unreferenced project zip in the staging area.
      let gpsDataUrl: string | undefined;
      if (selectedGpsFile) {
        setUploadStatus('Uploading GPS flight path...');
        const gpsSignedUrl = await getSignedUrlForDroneZip(
          parseInt(selectedProject), selectedDroneType, formattedDate, selectedGpsFile.name, 'gps'
        );
        await uploadZipToGCS(selectedGpsFile, gpsSignedUrl.signed_url, undefined, 'application/gpx+xml');
        gpsDataUrl = gpsSignedUrl.gcs_url;
      }

      // Step 1: Get signed URL
      console.log('🔑 Step 1: Requesting signed URL...');
      setUploadStatus('Getting upload URL...');
      const signedUrlData = await getSignedUrlForDroneZip(
        parseInt(selectedProject),
        selectedDroneType,
        formattedDate,
        selectedFile.name
      );
      console.log('✅ Step 1: Signed URL received:', signedUrlData.gcs_url);

      // Step 2: Upload zip to GCS
      console.log('📤 Step 2: Uploading to GCS...');
      setUploadStatus('Uploading zip file...');
      await uploadZipToGCS(selectedFile, signedUrlData.signed_url, (progress) => {
        setUploadProgress(progress);
        setUploadStatus(`Uploading zip file: ${Math.floor(progress)}%`);
      });
      console.log('✅ Step 2: Upload to GCS complete');

      // Step 3: Queue create request in background (do not block the user)
      console.log('💾 Step 3: Queueing drone video record creation in background...');
      setUploadProgress(100);
      const createPayload = {
        project: parseInt(selectedProject),
        drone_type: selectedDroneType,
        date: formattedDate,
        uploaded_zip_url: signedUrlData.gcs_url,
        gps_data_url: gpsDataUrl,
      };

      setSuccess('File uploaded successfully. It will be available in a few minutes.');
      resetFormAfterUpload();
      void finalizeDroneRecordInBackground(createPayload);
      return;
    } catch (err: any) {
      console.error('❌ Error uploading drone video:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        responseStatus: err.response?.status,
        responseHeaders: err.response?.headers,
        fullError: err
      });
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to upload drone video';
      setError(errorMessage);
    } finally {
      console.log('🏁 Upload process finished');
      setIsUploading(false);
    }
  };

  // Check if user is authenticated and has permission
  if (!user) {
    return (
      <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
        <h1 className="text-3xl font-bold mb-6">Upload Drone Video</h1>
        <AlertMessage
          type="error"
          message="You must be logged in to access this page. Please log in and try again."
        />
      </div>
    );
  }

  if (!user.is_staff) {
    return (
      <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
        <h1 className="text-3xl font-bold mb-6">Upload Drone Video</h1>
        <AlertMessage
          type="error"
          message="You must be a staff member to upload drone videos. Please contact your administrator for access."
        />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
      <h1 className="text-3xl font-bold mb-6">Upload Drone Video</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <ClientSelector
          clients={clients}
          value={selectedClient}
          onChange={setSelectedClient}
        />

        <ProjectSelector
          projects={projects}
          value={selectedProject}
          onChange={setSelectedProject}
          disabled={!selectedClient}
          isLoading={isLoadingProjects}
        />

        <DroneTypeSelector
          droneTypes={droneTypes}
          value={selectedDroneType}
          onChange={setSelectedDroneType}
          disabled={!selectedProject}
          isLoading={isLoadingTypes}
        />

        <DateInput
          value={date}
          onChange={setDate}
          disabled={isUploading}
        />

        <ZipFileUpload
          selectedFile={selectedFile}
          onChange={setSelectedFile}
          disabled={isUploading}
        />

        <GpsFileUpload
          selectedFile={selectedGpsFile}
          onChange={setSelectedGpsFile}
          disabled={isUploading}
        />

        {isUploading && (
          <UploadProgress progress={uploadProgress} status={uploadStatus} />
        )}

        <Button
          type="submit"
          disabled={isUploading}
          className="w-full"
          onClick={() => {
            console.log('🖱️ Button clicked');
            // Don't call handleSubmit here - let the form onSubmit handle it
          }}
        >
          {isUploading ? 'Uploading...' : 'Upload Drone Video'}
        </Button>

        {error && <AlertMessage type="error" message={error} />}
        {success && <AlertMessage type="success" message={success} />}
      </form>
    </div>
  );
};
